import { useState, useCallback, useEffect } from 'react';
import Portal from '@/components/Portal';
import Tooltip from '@/components/Tooltip';
import useScrollBlock from '@/hooks/useScrollBlock';
import DownloadProgramForm from './DownloadProgramForm';
import DownloadProgramNow from './DownloadProgramNow';
import DownloadProgramTestimonial from './DownloadProgramTestimonial';
import { useDownloadProgram } from './DownloadProgramContext';

export default function DownloadProgramModal() {
  const [isAbleToDownloadProgram, setAbleDownloadProgram] = useState(false);

  const {
    portalId,
    formUrl: formId,
    programUrl,
    trainingName,
    showDownloadProgramModal,
    modalDescription,
    modalTitle,
    testimonial,
    onHideModal,
  } = useDownloadProgram();

  const onDownloadProgramSuccess = useCallback(
    () => setAbleDownloadProgram(true),
    []
  );

  const [blockScroll, allowScroll] = useScrollBlock();

  useEffect(() => {
    if (showDownloadProgramModal) {
      blockScroll();
      return () => allowScroll();
    }
  }, [showDownloadProgramModal, blockScroll, allowScroll]);

  if (showDownloadProgramModal === false) {
    return null;
  }

  const formUrl = `${portalId}/${formId}`;

  return (
    <Portal>
      <div
        className="fixed inset-0 z-50"
        tabIndex="-1"
        role="dialog"
        aria-labelledby="Descargar programa de entrenamiento"
        aria-modal="true"
      >
        <div
          className="pointer-events-none absolute inset-0 z-0 bg-gray-900 opacity-90"
          style={{ backdropFilter: 'blur(20px) saturate(120%)' }}
        />
        <div className="absolute z-20 inset-0 flex items-center justify-center overflow-hidden">
          <div
            className="relative bg-white shadow-xl lg:shadow-2xl w-full xl:max-w-screen-xl lg:mx-6 lg:my-10 lg:h-full overflow-hidden max-h-full max-w-full flex"
            role="document"
          >
            <Tooltip tooltip="Cerrar">
              <button
                className="absolute right-0 top-0 m-4 w-10 h-10 flex items-center justify-center rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 hover:bg-white hover:shadow focus:bg-white focus:ring-green-200 text-gray-400 hover:text-gray-800 focus:text-gray-700"
                onClick={onHideModal}
                type="button"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
            </Tooltip>
            {isAbleToDownloadProgram && (
              <div className="min-h-full overflow-y-auto max-h-full w-full flex flex-col justify-center">
                <DownloadProgramNow
                  programUrl={programUrl}
                  trainingName={trainingName}
                />
              </div>
            )}
            {!isAbleToDownloadProgram && (
              <div className="grid grid-cols-1 min-h-full md:grid-cols-2 lg:grid-cols-12 max-h-full overflow-hidden">
                <div className="col-span-1 lg:col-span-5 hidden md:flex overflow-y-auto max-h-full">
                  <DownloadProgramTestimonial
                    modalDescription={modalDescription}
                    modalTitle={modalTitle}
                    testimonial={testimonial}
                  />
                </div>
                <div className="col-span-1 lg:col-span-7 flex lg:rounded-r-lg xl:rounded-r-xl pt-16 lg:pt-4 max-h-full overflow-hidden bg-white md:bg-gray-100">
                  <div className="w-full flex h-full lg:items-center overflow-y-auto">
                    <div className="p-6 lg:py-10 lg:px-10 bg-gray-100">
                      <DownloadProgramForm
                        formUrl={formUrl}
                        onSuccess={onDownloadProgramSuccess}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Portal>
  );
}
